/**
 * Date-period resolver: the single place that turns a period preset
 * ("this month", "last 7 days", ...) into concrete start/end dates.
 *
 * Two user settings shape the calendar periods:
 *  - `weekStartsOn`   — first day of the week (0 = Sunday ... 6 = Saturday)
 *  - `monthStartDay`  — the day an "accounting month" starts (1-28), e.g. payday
 *
 * All boundaries are computed in local time. `end` is the last millisecond of
 * the period's last day, so an inclusive `start <= date <= end` comparison
 * captures every expense on that day.
 *
 * Kept dependency-free on purpose: the same file is mirrored in
 * `packages/shared/src/utils/date-period.ts` for the web app.
 */

/** 0 = Sunday ... 6 = Saturday, matching `Date.prototype.getDay()`. */
export type WeekStartDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const MIN_MONTH_START_DAY = 1;
/** Capped at 28 so the start day exists in every month (no February clamping). */
export const MAX_MONTH_START_DAY = 28;
export const DEFAULT_MONTH_START_DAY = 1;
export const DEFAULT_WEEK_START_DAY: WeekStartDay = 1;

/**
 * An accounting month is named after the calendar month holding most of its
 * days: start day <= 15 -> the month it starts in, otherwise the following one.
 */
export const MONTH_NAMING_THRESHOLD_DAY = 15;

export interface PeriodSettings {
  weekStartsOn: WeekStartDay;
  monthStartDay: number;
}

export const DEFAULT_PERIOD_SETTINGS: PeriodSettings = {
  weekStartsOn: DEFAULT_WEEK_START_DAY,
  monthStartDay: DEFAULT_MONTH_START_DAY,
};

export type PeriodPreset =
  | 'today'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'
  | 'last7Days'
  | 'last30Days'
  | 'last3Months'
  | 'last6Months'
  | 'last12Months'
  | 'all';

/** Inclusive local-time range. */
export interface DatePeriod {
  start: Date;
  end: Date;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Regions whose weeks start on Sunday or Saturday (subset of CLDR weekData
 * firstDay). Everything else defaults to Monday.
 */
const SUNDAY_FIRST_REGIONS = new Set([
  'AG', 'AS', 'AU', 'BD', 'BR', 'BS', 'BT', 'BW', 'BZ', 'CA', 'CN', 'CO', 'DM', 'DO',
  'ET', 'GT', 'GU', 'HK', 'HN', 'ID', 'IL', 'IN', 'JM', 'JP', 'KE', 'KH', 'KR', 'LA',
  'MH', 'MM', 'MO', 'MT', 'MX', 'MZ', 'NI', 'NP', 'PA', 'PE', 'PH', 'PK', 'PR', 'PT',
  'PY', 'SA', 'SG', 'SV', 'TH', 'TT', 'TW', 'UM', 'US', 'VE', 'VI', 'WS', 'YE', 'ZA', 'ZW',
]);
const SATURDAY_FIRST_REGIONS = new Set([
  'AE', 'AF', 'BH', 'DJ', 'DZ', 'EG', 'IQ', 'IR', 'JO', 'KW', 'LY', 'OM', 'QA', 'SD', 'SY',
]);

// ---------------------------------------------------------------------------
// Settings normalization
// ---------------------------------------------------------------------------

export function isWeekStartDay(value: unknown): value is WeekStartDay {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6;
}

/** Returns a valid week start day or `null` when the value is unset/invalid. */
export function normalizeWeekStartDay(value: unknown): WeekStartDay | null {
  return isWeekStartDay(value) ? value : null;
}

/** Clamps to 1-28; unset/invalid values fall back to the default (1). */
export function normalizeMonthStartDay(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return DEFAULT_MONTH_START_DAY;
  }
  const rounded = Math.round(value);
  return Math.min(MAX_MONTH_START_DAY, Math.max(MIN_MONTH_START_DAY, rounded));
}

/** Week start implied by a BCP 47 locale tag such as `cs-CZ` or `en-US`. */
export function getDefaultWeekStartDay(localeTag: string | null | undefined): WeekStartDay {
  const region = extractRegion(localeTag);
  if (region && SUNDAY_FIRST_REGIONS.has(region)) return 0;
  if (region && SATURDAY_FIRST_REGIONS.has(region)) return 6;
  return DEFAULT_WEEK_START_DAY;
}

/**
 * Merges stored user preferences with locale defaults.
 * `weekStartDay: null` means "automatic" (derived from the locale).
 */
export function resolvePeriodSettings(
  stored: { weekStartDay?: number | null; monthStartDay?: number | null } | null | undefined,
  localeTag: string | null | undefined
): PeriodSettings {
  const weekStartsOn = normalizeWeekStartDay(stored?.weekStartDay) ?? getDefaultWeekStartDay(localeTag);
  const monthStartDay = normalizeMonthStartDay(stored?.monthStartDay);
  return { weekStartsOn, monthStartDay };
}

function extractRegion(localeTag: string | null | undefined): string | null {
  if (!localeTag) return null;
  // "cs-CZ", "en_US", "zh-Hant-TW" -> the 2-letter uppercase region subtag
  const parts = localeTag.replace(/_/g, '-').split('-');
  for (let i = 1; i < parts.length; i++) {
    if (/^[A-Za-z]{2}$/.test(parts[i])) return parts[i].toUpperCase();
  }
  return null;
}

// ---------------------------------------------------------------------------
// Day / week helpers (local time)
// ---------------------------------------------------------------------------

export function startOfDayLocal(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function endOfDayLocal(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days,
    date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
}

export function startOfWeekLocal(date: Date, weekStartsOn: WeekStartDay): Date {
  const day = date.getDay();
  const diff = (day - weekStartsOn + 7) % 7;
  return startOfDayLocal(addDays(date, -diff));
}

export function endOfWeekLocal(date: Date, weekStartsOn: WeekStartDay): Date {
  return endOfDayLocal(addDays(startOfWeekLocal(date, weekStartsOn), 6));
}

// ---------------------------------------------------------------------------
// Accounting month / year
// ---------------------------------------------------------------------------

/** First day of the accounting month that contains `date`. */
export function startOfAccountingMonth(date: Date, monthStartDay: number): Date {
  const startDay = normalizeMonthStartDay(monthStartDay);
  const monthOffset = date.getDate() >= startDay ? 0 : -1;
  return new Date(date.getFullYear(), date.getMonth() + monthOffset, startDay);
}

/** Last millisecond of the accounting month that contains `date`. */
export function endOfAccountingMonth(date: Date, monthStartDay: number): Date {
  const start = startOfAccountingMonth(date, monthStartDay);
  const nextStart = addAccountingMonths(start, 1);
  return new Date(nextStart.getTime() - 1);
}

/** Shifts an accounting-month start by whole months (the day-of-month is kept). */
export function addAccountingMonths(periodStart: Date, months: number): Date {
  return new Date(periodStart.getFullYear(), periodStart.getMonth() + months, periodStart.getDate());
}

/**
 * The calendar month an accounting month is named after, as the first day of
 * that month (see MONTH_NAMING_THRESHOLD_DAY). With start day 1 this is the
 * period start itself.
 */
export function accountingMonthAnchor(periodStart: Date, monthStartDay: number): Date {
  const startDay = normalizeMonthStartDay(monthStartDay);
  const monthOffset = startDay <= MONTH_NAMING_THRESHOLD_DAY ? 0 : 1;
  return new Date(periodStart.getFullYear(), periodStart.getMonth() + monthOffset, 1);
}

/**
 * First day of the accounting year containing `date`: the accounting month
 * named "January" of that year, so a year is always exactly 12 named months.
 */
export function startOfAccountingYear(date: Date, monthStartDay: number): Date {
  const startDay = normalizeMonthStartDay(monthStartDay);
  const monthStart = startOfAccountingMonth(date, startDay);
  const anchor = accountingMonthAnchor(monthStart, startDay);
  const year = anchor.getFullYear();
  return startDay <= MONTH_NAMING_THRESHOLD_DAY
    ? new Date(year, 0, startDay)
    : new Date(year - 1, 11, startDay);
}

/** Last millisecond of the accounting year containing `date`. */
export function endOfAccountingYear(date: Date, monthStartDay: number): Date {
  const start = startOfAccountingYear(date, monthStartDay);
  const nextStart = addAccountingMonths(start, 12);
  return new Date(nextStart.getTime() - 1);
}

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

/**
 * Resolves a preset to an inclusive local-time range, or `null` for "all time".
 *
 * Rolling presets count calendar days including today ("last 7 days" = today
 * and the 6 days before it); rolling month presets go back to the same
 * day-of-month N months ago.
 */
export function resolvePeriod(
  preset: PeriodPreset,
  settings: PeriodSettings = DEFAULT_PERIOD_SETTINGS,
  now: Date = new Date()
): DatePeriod | null {
  const { weekStartsOn, monthStartDay } = settings;
  const todayStart = startOfDayLocal(now);
  const todayEnd = endOfDayLocal(now);

  switch (preset) {
    case 'all':
      return null;

    case 'today':
      return { start: todayStart, end: todayEnd };

    case 'thisWeek':
      return {
        start: startOfWeekLocal(now, weekStartsOn),
        end: endOfWeekLocal(now, weekStartsOn),
      };

    case 'lastWeek': {
      const lastWeekDay = addDays(now, -7);
      return {
        start: startOfWeekLocal(lastWeekDay, weekStartsOn),
        end: endOfWeekLocal(lastWeekDay, weekStartsOn),
      };
    }

    case 'thisMonth':
      return {
        start: startOfAccountingMonth(now, monthStartDay),
        end: endOfAccountingMonth(now, monthStartDay),
      };

    case 'lastMonth': {
      const previousStart = addAccountingMonths(startOfAccountingMonth(now, monthStartDay), -1);
      return {
        start: previousStart,
        end: endOfAccountingMonth(previousStart, monthStartDay),
      };
    }

    case 'thisYear':
      return {
        start: startOfAccountingYear(now, monthStartDay),
        end: endOfAccountingYear(now, monthStartDay),
      };

    case 'lastYear': {
      const previousStart = addAccountingMonths(startOfAccountingYear(now, monthStartDay), -12);
      return {
        start: previousStart,
        end: endOfAccountingYear(previousStart, monthStartDay),
      };
    }

    case 'last7Days':
      return { start: addDays(todayStart, -6), end: todayEnd };

    case 'last30Days':
      return { start: addDays(todayStart, -29), end: todayEnd };

    case 'last3Months':
      return { start: startOfDayLocal(addMonthsClamped(now, -3)), end: todayEnd };

    case 'last6Months':
      return { start: startOfDayLocal(addMonthsClamped(now, -6)), end: todayEnd };

    case 'last12Months':
      return { start: startOfDayLocal(addMonthsClamped(now, -12)), end: todayEnd };

    default: {
      const exhaustiveCheck: never = preset;
      throw new Error(`Unknown period preset: ${String(exhaustiveCheck)}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Period browsing (previous / next period)
// ---------------------------------------------------------------------------

/** Calendar unit a preset naturally moves by when browsing, and how many of them. */
export function presetStep(preset: PeriodPreset): { unit: 'day' | 'week' | 'month' | 'year'; count: number } | null {
  switch (preset) {
    case 'today':
      return { unit: 'day', count: 1 };
    case 'thisWeek':
    case 'lastWeek':
      return { unit: 'week', count: 1 };
    case 'thisMonth':
    case 'lastMonth':
      return { unit: 'month', count: 1 };
    case 'thisYear':
    case 'lastYear':
      return { unit: 'year', count: 1 };
    case 'last7Days':
      return { unit: 'day', count: 7 };
    case 'last30Days':
      return { unit: 'day', count: 30 };
    case 'last3Months':
      return { unit: 'month', count: 3 };
    case 'last6Months':
      return { unit: 'month', count: 6 };
    case 'last12Months':
      return { unit: 'month', count: 12 };
    case 'all':
      return null;
    default: {
      const exhaustiveCheck: never = preset;
      throw new Error(`Unknown period preset: ${String(exhaustiveCheck)}`);
    }
  }
}

/**
 * Resolves a preset shifted by whole periods: offset -1 is the period before
 * the one containing `now`, +1 the one after. Offset 0 equals `resolvePeriod`.
 */
export function resolvePeriodWithOffset(
  preset: PeriodPreset,
  settings: PeriodSettings = DEFAULT_PERIOD_SETTINGS,
  offset: number = 0,
  now: Date = new Date()
): DatePeriod | null {
  const step = presetStep(preset);
  if (offset === 0 || step === null) {
    return resolvePeriod(preset, settings, now);
  }

  const { monthStartDay } = settings;
  let anchor: Date;
  switch (step.unit) {
    case 'day':
      anchor = addDays(now, offset * step.count);
      break;
    case 'week':
      anchor = addDays(now, offset * 7);
      break;
    case 'month':
      // Anchor on the first day of the target accounting month so the
      // day-of-month never overflows a shorter month.
      anchor = addAccountingMonths(startOfAccountingMonth(now, monthStartDay), offset * step.count);
      break;
    case 'year':
      anchor = addAccountingMonths(startOfAccountingYear(now, monthStartDay), offset * 12);
      break;
  }
  return resolvePeriod(preset, settings, anchor);
}

/** True when `date` falls inside the (inclusive) period. */
export function isWithinPeriod(date: Date, period: DatePeriod): boolean {
  const time = date.getTime();
  return time >= period.start.getTime() && time <= period.end.getTime();
}

/** Number of calendar days the period spans (at least 1). */
export function periodLengthInDays(period: DatePeriod): number {
  const start = startOfDayLocal(period.start).getTime();
  const end = startOfDayLocal(period.end).getTime();
  return Math.max(1, Math.round((end - start) / MS_PER_DAY) + 1);
}

/**
 * Same day-of-month N months away, clamped to the target month's length
 * (Mar 31 - 1 month -> Feb 28/29) so rolling ranges never overshoot.
 */
function addMonthsClamped(date: Date, months: number): Date {
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1,
    date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
  const daysInTarget = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(date.getDate(), daysInTarget));
  return target;
}
